import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getScheduledMessage, updateScheduledMessage } from '../../../api/scheduledMessages';
import { getScheduledMessageAdmin, updateScheduledMessageAdmin } from '../../../api/scheduledMessagesAdmin';
import { getApiErrorMessage } from '../../../utils/errors';
import ScheduledMessageFormFields from './ScheduledMessageFormFields';
import { ScheduleStatusBadge } from './StatusBadge';
import {
  buildListQueryString,
  buildSchedulePayload,
  buildScheduledMessagesPath,
  contactLabel,
  formStateFromScheduledMessage,
  isScheduledMessageEditable,
  isValidRecurrenceForm,
} from '../utils';

function ReadOnlyField({ label, children }) {
  return (
    <div>
      <dt className="text-xs font-medium text-content-muted">{label}</dt>
      <dd className="text-sm text-content-primary mt-1 break-all">{children}</dd>
    </div>
  );
}

function EditScheduledMessagePage({ mode }) {
  const { scheduledMessageId } = useParams();
  const [searchParams] = useSearchParams();
  const agentId = searchParams.get('agentId') || '';
  const navigate = useNavigate();

  const [item, setItem] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const listPath = `${buildScheduledMessagesPath(mode)}${buildListQueryString(agentId, '')}`;
  const detailPath = buildScheduledMessagesPath(mode, { agentId, scheduledMessageId });

  const loadItem = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data =
        mode === 'admin'
          ? await getScheduledMessageAdmin(scheduledMessageId)
          : await getScheduledMessage(scheduledMessageId);
      setItem(data);
      setForm(formStateFromScheduledMessage(data));
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível carregar o agendamento.'));
      setItem(null);
      setForm(null);
    } finally {
      setLoading(false);
    }
  }, [mode, scheduledMessageId]);

  useEffect(() => {
    loadItem();
  }, [loadItem]);

  const updateField = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const setFormFields = (partial) => {
    setForm((current) => ({ ...current, ...partial }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError('');

    if (!form.templateName.trim()) {
      setSubmitError('Informe o nome do template Botmaker.');
      return;
    }
    if (form.generationInstruction.trim().length < 10) {
      setSubmitError('Informe uma instrução para geração com pelo menos 10 caracteres.');
      return;
    }
    if (form.scheduleType === 'one_off' && !form.scheduledAt) {
      setSubmitError('Informe data e hora para agendamento único.');
      return;
    }
    if (form.scheduleType === 'recurring' && !isValidRecurrenceForm(form)) {
      setSubmitError(
        form.scheduleRecurrenceMode === 'advanced'
          ? 'Expressão cron inválida.'
          : 'Informe hora, minuto e pelo menos um dia da semana.'
      );
      return;
    }

    setSaving(true);
    try {
      const payload = buildSchedulePayload(form);
      if (mode === 'admin') {
        await updateScheduledMessageAdmin(scheduledMessageId, payload);
      } else {
        await updateScheduledMessage(scheduledMessageId, payload);
      }
      navigate(detailPath);
    } catch (err) {
      setSubmitError(getApiErrorMessage(err, 'Não foi possível salvar o agendamento.'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="surface-card px-4 py-12 text-center">
        <p className="text-content-muted text-sm">Carregando agendamento...</p>
      </div>
    );
  }

  if (error || !item || !form) {
    return (
      <div className="space-y-4">
        <Link to={listPath} className="text-sm text-forge-700 hover:text-forge-800 font-medium">
          ← Voltar aos agendamentos
        </Link>
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-sm text-red-700">{error || 'Agendamento não encontrado.'}</p>
        </div>
      </div>
    );
  }

  if (!isScheduledMessageEditable(item.status)) {
    return (
      <div className="space-y-4">
        <Link to={detailPath} className="text-sm text-forge-700 hover:text-forge-800 font-medium">
          ← Voltar ao detalhe
        </Link>
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
          <p className="text-sm text-amber-800">
            Este agendamento não pode ser editado no status atual.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <Link to={detailPath} className="text-sm text-forge-700 hover:text-forge-800 font-medium">
          ← Voltar ao detalhe
        </Link>
        <div className="flex flex-wrap items-center gap-2 mt-2">
          <h1 className="text-2xl font-bold text-content-primary">Editar agendamento</h1>
          <ScheduleStatusBadge status={item.status} />
        </div>
        <p className="text-content-muted text-sm mt-1">
          {contactLabel(item)} · {item.template_name}
        </p>
      </div>

      <div className="surface-card p-4 sm:p-5 space-y-4">
        <form onSubmit={handleSubmit} className="space-y-6">
          <section className="space-y-4">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-content-muted">
              Destinatário
            </h3>
            <dl className="grid gap-4 sm:grid-cols-2">
              {mode === 'admin' && (
                <ReadOnlyField label="Agente">{item.agent?.name || '—'}</ReadOnlyField>
              )}
              <ReadOnlyField label="Contato">{contactLabel(item)}</ReadOnlyField>
            </dl>
          </section>

          <ScheduledMessageFormFields
            form={form}
            updateField={updateField}
            setFormFields={setFormFields}
            fieldsDisabled={saving}
            idPrefix="edit-schedule"
          />

          {submitError && <p className="text-sm text-red-700">{submitError}</p>}

          <div className="flex flex-wrap justify-end gap-3 border-t border-surface-border pt-4">
            <Link to={detailPath} className="btn-secondary text-sm px-4 py-2">
              Cancelar
            </Link>
            <button
              type="submit"
              disabled={saving}
              className="btn-primary text-sm px-4 py-2 disabled:opacity-40"
            >
              {saving ? 'Salvando...' : 'Salvar alterações'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditScheduledMessagePage;
